import React, { useState, useEffect, useMemo } from 'react';
import { FONT_GROUPED_OPTIONS } from '../../utils/fonts';
import { DEVICE_ORDER, DEVICE_PRESETS } from '../../utils/devicePresets';

const hexToRgb = hex => {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const rgbToHex = (r, g, b) => {
  return (
    '#' +
    ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toLowerCase()
  );
};

export const UndoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="w-4 h-4"
  >
    <path d="M3 10h10a5 5 0 0 1 5 5v3M3 10l6-6M3 10l6 6" />
  </svg>
);
export const RedoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="w-4 h-4"
  >
    <path d="M21 10H11a5 5 0 0 0-5 5v3M21 10l-6-6M21 10l-6 6" />
  </svg>
);
export const CopyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
export const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

export const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 text-gray-500 hover:text-red-600 transition-colors"
  >
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
export const PasteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
  </svg>
);
export const CheckIcon = () => (
  <svg
    className="w-12 h-12 text-green-500 mx-auto"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    ></path>
  </svg>
);
export const ChevronUpIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M5 15l7-7 7 7"
    ></path>
  </svg>
);
export const ChevronDownIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 9l-7 7-7-7"
    ></path>
  </svg>
);
export const LogoutIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);
export const ClearIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19.512 10.488A9 9 0 1010.488 19.512M19.512 10.488L10.488 19.512m9.024-9.024a9 9 0 00-12.76 0M3.488 13.512a9 9 0 0012.76 0"
    />
  </svg>
);
export const ProfileIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

export const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 text-gray-700"
  >
    <ellipse cx="12" cy="12" rx="10" ry="6" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

const createSidebarIcon = paths => {
  const SidebarIcon = ({ className = '', ...props }) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`w-5 h-5 ${className}`}
      {...props}
    >
      {paths}
    </svg>
  );
  return SidebarIcon;
};

export const TemplatesIcon = createSidebarIcon(
  <>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </>
);

export const VisualEditorIcon = createSidebarIcon(
  <>
    <path d="M4 17l5-5-5-5" />
    <path d="M9 17h11" />
    <path d="M13 7l7 7" />
  </>
);

export const AppearanceIcon = createSidebarIcon(
  <>
    <circle cx="9" cy="9" r="3.5" />
    <path d="M14.5 4.5l5 5" />
    <path d="M12 13.5L7 19M12 13.5L17 19" />
  </>
);

export const ContentIcon = createSidebarIcon(
  <>
    <path d="M4 7h16M4 12h12M4 17h8" />
  </>
);

export const WalletIcon = createSidebarIcon(
  <>
    <path d="M4 7h16v10H4z" />
    <path d="M16 12h2" />
    <path d="M7 7V5a2 2 0 0 1 2-2h9" />
  </>
);

export const WalletImageResizerIcon = createSidebarIcon(
  <>
    <rect x="4" y="5" width="16" height="14" rx="2" />
    <path d="M8 9v6" />
    <path d="M16 9v6" />
    <path d="M9.5 12H6" />
    <path d="M18 12h-3.5" />
  </>
);

export const RegmailIcon = createSidebarIcon(
  <>
    <rect x="4" y="5" width="16" height="14" rx="2" />
    <path d="M5.5 7.5L12 12l6.5-4.5" />
  </>
);

export const LayoutIcon = createSidebarIcon(
  <>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="6.5" y="6.5" width="7" height="11" rx="1" />
    <path d="M14.5 6.5h3M14.5 10h3M14.5 13.5h3M14.5 17h3" />
  </>
);

export const ContactsIcon = createSidebarIcon(
  <>
    <circle cx="12" cy="10" r="3" />
    <path d="M7 19a5 5 0 0 1 10 0" />
    <path d="M5 5h4M5 8h2" />
  </>
);

export const AccessibilityIcon = createSidebarIcon(
  <>
    <circle cx="12" cy="5.5" r="2" />
    <path d="M4 9h16" />
    <path d="M9 9l1 10M15 9l-1 10" />
  </>
);

export const CalendarButtonIcon = createSidebarIcon(
  <>
    <rect x="3.5" y="5.5" width="17" height="14" rx="2" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="M6.5 11h4" />
    <path d="M13.5 15.5l3 3" />
    <circle cx="15.8" cy="13.2" r="2.3" />
  </>
);

export const GenerateIcon = createSidebarIcon(
  <>
    <path d="M8 8l-4 4 4 4" />
    <path d="M16 8l4 4-4 4" />
    <path d="M11 6l2 12" />
  </>
);

export const PinToggleIcon = ({ locked, className = '', ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`w-5 h-5 ${className}`}
    {...props}
  >
    {locked ? (
      <>
        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <circle cx="12" cy="16" r="1.5" />
      </>
    ) : (
      <>
        <path d="M7 10V7a5 5 0 0 1 9-3" />
        <path d="M16 5v5" />
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <circle cx="12" cy="16" r="1.5" />
      </>
    )}
  </svg>
);

export const SidebarCollapseIcon = ({ collapsed, className = '', ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`w-5 h-5 ${className}`}
    {...props}
  >
    {collapsed ? (
      <path d="M10 6l6 6-6 6" />
    ) : (
      <path d="M14 6l-6 6 6 6" />
    )}
  </svg>
);

export const Logo = () => (
  <div className="flex items-center gap-3">
    <div className="w-12 h-12 flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <rect width="100" height="100" rx="22" fill="var(--primary-color)" />
        <text
          x="50"
          y="55"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="52"
          fontWeight="bold"
        >
          R
        </text>
        <circle cx="80" cy="20" r="10" fill="var(--accent-color)" />
      </svg>
    </div>
    <div className="flex flex-col leading-tight">
      <span className="font-bold text-lg text-gray-800">REACT</span>
      <span className="text-sm text-gray-500">Email Configurator</span>
    </div>
  </div>
);

export const LogoMark = () => (
  <div className="w-12 h-12 flex items-center justify-center">
    <svg viewBox="0 0 100 100" className="w-10 h-10">
      <rect width="100" height="100" rx="22" fill="var(--primary-color)" />
      <text
        x="50"
        y="55"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize="52"
        fontWeight="bold"
      >
        R
      </text>
    </svg>
  </div>
);

export const Sidebar = ({
  activeSection,
  onNavigate,
  showWallet,
  showCalendar,
  showCongressAppLinks,
  logout,
  clearConfig,
  onProfileClick,
  settings,
  collapsed: collapsedProp = false,
  onCollapsedChange = () => {},
  locked: lockedProp = false,
  onLockedChange = () => {},
  isMobile = false,
  mobileOpen = true,
  onRequestCloseMobile = () => {},
}) => {
  const parseMaybeJson = value => {
    if (!value || typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const parsedData = parseMaybeJson(settings?.data);
  const collapsed = isMobile ? false : collapsedProp;
  const locked = isMobile ? false : lockedProp;

  const resolveExplicitRegmailFlag = source =>
    source &&
    typeof source === 'object' &&
    Object.prototype.hasOwnProperty.call(source, 'regmailEnabled')
      ? Boolean(source.regmailEnabled)
      : undefined;

  const regmailNavVisible = (() => {
    const explicitFromSettings = resolveExplicitRegmailFlag(settings);
    const explicitFromData = resolveExplicitRegmailFlag(parsedData);

    if (explicitFromSettings === true || explicitFromData === true) return true;
    if (explicitFromSettings === false || explicitFromData === false) return false;

    const categoryValue = settings?.category || parsedData?.category;
    if (
      categoryValue &&
      typeof categoryValue === 'string' &&
      categoryValue.toLowerCase().includes('regmail')
    )
      return true;
    const templateName = settings?.name || parsedData?.name;
    if (
      templateName &&
      typeof templateName === 'string' &&
      templateName.toLowerCase().includes('regmail')
    )
      return true;

    const dataToInspect =
      settings?.regmail || parsedData?.regmail || parsedData;
    if (dataToInspect && typeof dataToInspect === 'object') {
      const regmailKeys = [
        'eventName',
        'hostingOrganisation',
        'eventLocation',
        'eventStartDate',
        'eventEndDate',
        'eventTime',
        'paymentDueDate',
        'eventEmail',
      ];
      if (
        regmailKeys.some(key =>
          Object.prototype.hasOwnProperty.call(dataToInspect, key)
        )
      )
        return true;
    }

    return false;
  })();

  const transitionTiming = 'cubic-bezier(0.19, 1, 0.22, 1)';
  const transitionDurationMs = 420;

  const navItems = [
    { key: 'templateSelect', label: 'Templates', Icon: TemplatesIcon },
    { key: 'visualEditor', label: 'Visual Editor', Icon: VisualEditorIcon },
    { key: 'appearance', label: 'Appearance', Icon: AppearanceIcon },
    { key: 'content', label: 'Content & Text', Icon: ContentIcon },
    {
      key: 'calendarButton',
      label: 'Calendar Button',
      Icon: CalendarButtonIcon,
      hidden: !showCalendar,
    },
    {
      key: 'congressAppLinks',
      label: 'Congress App Links',
      Icon: ContentIcon,
      hidden: !showCongressAppLinks,
    },
    {
      key: 'wallet',
      label: 'Wallet Customization',
      Icon: WalletIcon,
      hidden: !showWallet,
    },
    {
      key: 'walletImageResizer',
      label: 'Wallet Image Resizer',
      Icon: WalletImageResizerIcon,
    },
    {
      key: 'regmailCustomization',
      label: 'Regmail Customization',
      Icon: RegmailIcon,
      hidden: !regmailNavVisible,
    },
    { key: 'images', label: 'Layout & Media', Icon: LayoutIcon },
    { key: 'contacts', label: 'Contact Information', Icon: ContactsIcon },
    { key: 'textTemplates', label: 'Text Templates', Icon: TemplatesIcon },
    { key: 'accessibility', label: 'Accessibility Checker', Icon: AccessibilityIcon },
    { key: 'generate', label: 'Generate HTML', Icon: GenerateIcon },
  ];

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (!locked && collapsed) onCollapsedChange(false);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    if (!locked && !collapsed) onCollapsedChange(true);
  };

  const handleToggleLock = () => {
    if (isMobile) return;
    const nextLocked = !lockedProp;
    onLockedChange(nextLocked);
    if (nextLocked) onCollapsedChange(false);
  };

  const handleToggleCollapsed = () => {
    if (isMobile) return;
    const nextCollapsed = !collapsedProp;
    onCollapsedChange(nextCollapsed);
    if (nextCollapsed && lockedProp) onLockedChange(false);
  };

  const sidebarWidth = collapsed ? '5rem' : '20rem';
  const rootClasses = [
    'glass-panel fixed top-0 left-0 flex h-screen min-h-screen flex-col text-slate-700 shadow-xl transition-transform duration-300 ease-in-out transform-gpu',
    isMobile ? 'z-[60]' : '',
    isMobile ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') : '',
  ]
    .filter(Boolean)
    .join(' ');
  const headerClasses = [
    'border-b border-slate-200 transition-all duration-300 ease-in-out',
    collapsed
      ? 'px-3 py-4 flex flex-col items-center gap-3'
      : 'px-4 py-3 flex flex-wrap items-center justify-between gap-2',
  ].join(' ');
  const controlsWrapperClasses = collapsed
    ? 'flex flex-col items-center gap-2 rounded-full bg-slate-100/60 px-1.5 py-1.5 transition-all duration-300 ease-in-out'
    : 'flex max-w-full flex-wrap items-center justify-end gap-1.5 rounded-full bg-slate-100/60 px-2 py-1.5 transition-all duration-300 ease-in-out';
  const controlBase =
    'flex items-center gap-2 rounded-full transition-all duration-300 ease-in-out text-slate-500 hover:text-primary hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary/30';
  const lockButtonClass = `${controlBase} ${collapsed ? 'px-2 py-2' : 'px-2.5 py-1'} ${
    locked ? 'text-primary bg-white shadow-sm' : ''
  }`;
  const collapseButtonClass = `${controlBase} ${collapsed ? 'px-2 py-2' : 'px-2.5 py-1'} ${
    collapsed ? 'text-primary bg-white shadow-sm' : ''
  }`;
  const navLabelClasses = `flex-1 origin-left overflow-hidden whitespace-nowrap text-left transition-all duration-300 ease-in-out ${
    collapsed
      ? 'ml-0 max-w-0 opacity-0 scale-95 -translate-x-1 pointer-events-none'
      : 'ml-2 max-w-xs opacity-100 scale-100 translate-x-0'
  }`;
  const footerLabelClasses = `overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out ${
    collapsed
      ? 'ml-0 opacity-0 -translate-x-1 max-w-0 pointer-events-none'
      : 'ml-1.5 opacity-100 translate-x-0 max-w-[64px]'
  }`;

  const sidebarStyle = isMobile
    ? {
        width: 'min(320px, 100vw)',
        maxWidth: '90vw',
      }
    : {
        width: sidebarWidth,
        transition: `width ${transitionDurationMs}ms ${transitionTiming}`,
      };

  return (
    <aside
      id="kit-config-sidebar"
      className={rootClasses}
      style={sidebarStyle}
      aria-hidden={isMobile && !mobileOpen}
      aria-label="Configurator sections"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={headerClasses}
        style={{ transition: `all ${transitionDurationMs}ms ${transitionTiming}` }}
      >
        <div className="flex items-center gap-3">
          {collapsed ? <LogoMark /> : <Logo />}
        </div>
        {isMobile ? (
          <button
            type="button"
            onClick={onRequestCloseMobile}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 py-1 text-sm font-semibold text-slate-600 transition hover:border-primary/40 hover:text-primary"
            aria-label="Close navigation"
          >
            × Close
          </button>
        ) : (
          <div
            className={controlsWrapperClasses}
            style={{ transition: `all ${transitionDurationMs}ms ${transitionTiming}` }}
          >
            <button
              type="button"
              onClick={handleToggleLock}
              className={lockButtonClass}
              title={locked ? 'Unlock sidebar' : 'Lock sidebar'}
            >
              <PinToggleIcon locked={locked} className={locked ? 'text-primary' : ''} />
              <span
                className={`text-xs font-semibold ${footerLabelClasses}`}
                aria-hidden={collapsed}
                style={{
                  transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
                }}
              >
                {locked ? 'Pinned' : 'Pin'}
              </span>
            </button>
            <button
              type="button"
              onClick={handleToggleCollapsed}
              className={collapseButtonClass}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <SidebarCollapseIcon collapsed={collapsed} className={collapsed ? 'text-primary' : ''} />
              <span
                className={`text-xs font-semibold ${footerLabelClasses}`}
                aria-hidden={collapsed}
                style={{
                  transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
                }}
              >
                {collapsed ? 'Expand' : 'Collapse'}
              </span>
            </button>
          </div>
        )}
      </div>
      <nav
        className={`${collapsed ? 'px-2 py-3' : 'px-3 py-4'} flex-grow overflow-y-auto transition-all duration-300 ease-in-out`}
        style={{ transition: `all ${transitionDurationMs}ms ${transitionTiming}` }}
      >
        <ul className="space-y-1.5">
          {navItems
            .filter(item => !item.hidden)
            .map(({ key, label, Icon }) => {
              const isActive = activeSection === key;
              const buttonClasses = [
                'w-full flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-in-out overflow-hidden',
                collapsed ? 'justify-center gap-0' : 'justify-start gap-3',
                isActive ? 'bg-primary/15 text-primary' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              ].join(' ');
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => {
                      onNavigate(key);
                      if (isMobile) onRequestCloseMobile();
                    }}
                    className={buttonClasses}
                    title={label}
                  >
                    <Icon className={`${isActive ? 'text-primary' : 'text-slate-400'} shrink-0`} />
                    <span
                      className={navLabelClasses}
                      style={{
                        transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
                      }}
                    >
                      {label}
                    </span>
                  </button>
                </li>
              );
            })}
        </ul>
      </nav>
      <div
        className={`${collapsed ? 'px-2 py-3' : 'px-4 py-4'} border-t border-slate-200 space-y-3 transition-all duration-300 ease-in-out`}
        style={{ transition: `all ${transitionDurationMs}ms ${transitionTiming}` }}
      >
        <button
          onClick={() => {
            onProfileClick();
            if (isMobile) onRequestCloseMobile();
          }}
          className="w-full transition-colors rounded-xl bg-white/80 hover:bg-white flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-primary py-2"
          title="My Profile"
        >
          <ProfileIcon />
          <span
            className={footerLabelClasses}
            style={{
              transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
            }}
          >
            My Profile
          </span>
        </button>
        <button
          onClick={() => {
            logout();
            if (isMobile) onRequestCloseMobile();
          }}
          className="w-full transition-colors rounded-xl bg-white/80 hover:bg-white flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-primary py-2"
          title="Logout"
        >
          <LogoutIcon />
          <span
            className={footerLabelClasses}
            style={{
              transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
            }}
          >
            Logout
          </span>
        </button>
        <button
          onClick={() => {
            if (
              window.confirm(
                'Are you sure you want to clear all settings and start over? This cannot be undone.'
              )
            ) {
              clearConfig();
              if (isMobile) onRequestCloseMobile();
            }
          }}
          className="w-full transition-colors rounded-xl hover:bg-rose-100 flex items-center justify-center gap-2 text-sm text-rose-600 py-2 bg-rose-50"
          title="Clear all settings"
        >
          <ClearIcon />
          <span
            className={footerLabelClasses}
            style={{
              transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, transform ${transitionDurationMs}ms ${transitionTiming}, max-width ${transitionDurationMs}ms ${transitionTiming}, margin-left ${transitionDurationMs}ms ${transitionTiming}`,
            }}
          >
            Clear All
          </span>
        </button>
        <div
          className={`text-xs text-center text-gray-400 mt-2 transition-all duration-300 ease-in-out ${
            collapsed ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-16'
          }`}
          style={{
            transition: `opacity ${transitionDurationMs}ms ${transitionTiming}, max-height ${transitionDurationMs}ms ${transitionTiming}`,
          }}
        >
          <p>K.I.T. Group GmbH</p>
          <p>© 2026</p>
        </div>
      </div>
    </aside>
  );
};

export const Section = ({ title, description, isActive, children }) => {
  if (!isActive) return null;
  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);
  const hasHeader = hasTitle || hasDescription;
  return (
    <div className="bg-white p-5 sm:p-6 lg:p-8 rounded-2xl shadow-sm ring-1 ring-slate-900/5 animate-fadeIn w-full">
      {hasHeader && (
        <div className="mb-6 sm:mb-8">
          {hasTitle && (
            <h2 className={`text-2xl font-bold text-gray-900 ${hasDescription ? 'mb-1' : ''}`}>
              {title}
            </h2>
          )}
          {hasDescription && <p className="text-gray-500">{description}</p>}
        </div>
      )}
      <div className="space-y-6 sm:space-y-8">{children}</div>
    </div>
  );
};

export const FormGroup = ({ title, children, className }) => (
  <div className={className}>
    {title && (
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
    )}
    <div className="space-y-6">{children}</div>
  </div>
);

export const NavButtons = ({
  onPrev,
  onNext,
  prevLabel = 'Previous',
  nextLabel = 'Next',
}) => (
  <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <button type="button" onClick={onPrev} className="nav-button-secondary w-full sm:w-auto">
      {prevLabel}
    </button>
    <button type="button" onClick={onNext} className="nav-button-primary w-full sm:w-auto">
      {nextLabel}
    </button>
  </div>
);

export const ToggleSwitch = ({ label, checked, onChange, tooltip }) => (
  <div className="flex items-center justify-between gap-4 p-4 bg-gray-50/80 rounded-lg border border-gray-200/80">
    <label className="text-sm font-medium text-gray-800 flex items-center gap-2 leading-5">
      {label}
      {tooltip && (
        <span className="text-gray-400 cursor-help relative group">
          ?
          <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 p-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {tooltip}
          </span>
        </span>
      )}
    </label>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-gray-300'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  </div>
);

const PasteButton = ({ onPaste }) => (
  <button
    type="button"
    onClick={onPaste}
    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
  >
    <PasteIcon />
  </button>
);

export const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  helpText,
  type = 'text',
  onPaste,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const disableOverlayPlaceholder = ['date', 'datetime-local', 'time'].includes(type);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (onChange) {
        onChange({ target: { value: text } });
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const showPlaceholder =
    !disableOverlayPlaceholder && placeholder && !isFocused && !value;

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {showPlaceholder && (
          <span
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition-opacity duration-300 pointer-events-none"
          >
            {placeholder}
          </span>
        )}
        <input
          value={value || ''}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
          type={type}
          placeholder={disableOverlayPlaceholder ? placeholder : undefined}
          className="form-input bg-transparent"
          {...props}
        />
        {onPaste && <PasteButton onPaste={handlePaste} />}
      </div>
      {helpText && <p className="mt-1.5 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
};

export const FormSelect = ({
  label,
  value,
  onChange,
  children,
  helpText,
  ...props
}) => {
  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <select
        value={value || ''}
        onChange={onChange}
        className="form-select"
        {...props}
      >
        {children}
      </select>
      {helpText && <p className="mt-1.5 text-xs text-gray-500">{helpText}</p>}
    </div>
  );
};

export const AdvancedColorInput = ({ label, value, onChange }) => {
  const [rgb, setRgb] = useState(hexToRgb(value) || { r: 0, g: 0, b: 0 });
  const [recentColors, setRecentColors] = useState(() => {
    try {
      const raw = localStorage.getItem('kit:recentColors');
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });
  const [recentsLocked, setRecentsLocked] = useState(() => {
    try {
      return localStorage.getItem('kit:recentColorsLocked') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setRgb(hexToRgb(value) || { r: 0, g: 0, b: 0 });
  }, [value]);

  // Sync recents across multiple color inputs on the page
  useEffect(() => {
    const syncRecents = () => {
      try {
        const raw = localStorage.getItem('kit:recentColors');
        const arr = raw ? JSON.parse(raw) : [];
        setRecentColors(Array.isArray(arr) ? arr : []);
      } catch {}
    };
    const syncLock = () => {
      try {
        setRecentsLocked(localStorage.getItem('kit:recentColorsLocked') === 'true');
      } catch {}
    };
    window.addEventListener('kit:recentColors', syncRecents);
    window.addEventListener('kit:recentColorsLock', syncLock);
    return () => {
      window.removeEventListener('kit:recentColors', syncRecents);
      window.removeEventListener('kit:recentColorsLock', syncLock);
    };
  }, []);

  const isValidHex = hex => /^#([0-9a-fA-F]{6})$/.test(hex || '');

  const broadcastLock = next => {
    try {
      localStorage.setItem('kit:recentColorsLocked', next ? 'true' : 'false');
      setRecentsLocked(next);
      window.dispatchEvent(new Event('kit:recentColorsLock'));
    } catch {}
  };

  const saveRecents = list => {
    try {
      localStorage.setItem('kit:recentColors', JSON.stringify(list));
      setRecentColors(list);
      window.dispatchEvent(new Event('kit:recentColors'));
    } catch {}
  };

  const addRecent = hex => {
    if (recentsLocked) return;
    if (!isValidHex(hex)) return;
    const lowered = String(hex).toLowerCase();
    const next = [lowered, ...recentColors.filter(c => c.toLowerCase() !== lowered)].slice(0, 10);
    saveRecents(next);
  };

  const clearRecents = () => {
    if (recentsLocked) return;
    saveRecents([]);
  };

  const handleHexChange = e => {
    const hex = e.target.value;
    onChange(hex);
    if (isValidHex(hex)) addRecent(hex);
  };

  const handleRgbChange = (color, val) => {
    const newRgb = { ...rgb, [color]: parseInt(val) || 0 };
    setRgb(newRgb);
    const hex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    onChange(hex);
    if (isValidHex(hex)) addRecent(hex);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2 p-2 border border-gray-200/80 rounded-lg bg-gray-50/30">
        <input
          type="color"
          value={value}
          onChange={handleHexChange}
          className="p-0 w-10 h-10 border border-gray-300 rounded-md cursor-pointer bg-transparent"
        />
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            HEX
          </span>
          <input
            type="text"
            value={value}
            onChange={handleHexChange}
            className="form-input w-28 pl-9 text-center"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400 ml-2">RGB</span>
          {['r', 'g', 'b'].map(c => (
            <input
              key={c}
              type="number"
              min="0"
              max="255"
              value={rgb[c]}
              onChange={e => handleRgbChange(c, e.target.value)}
              className="form-input w-20 text-center"
            />
          ))}
        </div>
      </div>

      {recentColors.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Recent</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearRecents}
                disabled={recentsLocked}
                className={`text-xs ${recentsLocked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => broadcastLock(!recentsLocked)}
                className={`text-xs ${recentsLocked ? 'text-primary font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {recentsLocked ? 'Unlock' : 'Lock'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recentColors.map(c => (
              <button
                key={c}
                type="button"
                title={c}
                onClick={() => {
                  onChange(c);
                  addRecent(c);
                }}
                className={`w-6 h-6 rounded border ${c.toLowerCase() === String(value || '').toLowerCase() ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          {recentsLocked && (
            <p className="mt-1 text-xs text-amber-600">
              Palette locked – unlock to record new colors.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const Slider = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit = 'px',
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-slider"
        style={{ '--slider-value': `${((value - min) / (max - min)) * 100}%` }}
      />
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          className="w-20 text-center pl-2 pr-7 py-1.5 border border-gray-300 rounded-md shadow-sm sm:text-sm"
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
          {unit}
        </span>
      </div>
    </div>
  </div>
);

export const StyleButtons = ({ styles = [], onToggle, targetId }) => {
  const handleToggle = style => {
    const currentStyles = styles || [];
    let newStyles;

    if (style === 'normal') {
      newStyles = ['normal'];
    } else {
      newStyles = currentStyles.includes('normal') ? [] : [...currentStyles];
      if (newStyles.includes(style)) {
        newStyles = newStyles.filter(s => s !== style);
      } else {
        newStyles.push(style);
      }
      if (newStyles.length === 0) newStyles = ['normal'];
    }
    onToggle(newStyles);
  };

  return (
    <div className="flex bg-gray-100/80 p-1 rounded-lg w-max">
      {['bold', 'italic', 'underline', 'normal'].map(style => (
        <button
          key={`${targetId}-${style}`}
          onClick={() => handleToggle(style)}
          className={`w-9 h-9 border-transparent rounded-md flex items-center justify-center transition-all duration-200 ${(styles || []).includes(style) ? 'bg-white text-primary shadow-sm' : 'bg-transparent text-gray-600 hover:bg-white/60'}`}
        >
          {style === 'bold' && <span className="font-bold">B</span>}
          {style === 'italic' && <i className="font-serif text-lg">I</i>}
          {style === 'underline' && <u className="font-serif">U</u>}
          {style === 'normal' && <span className="text-xs">N</span>}
        </button>
      ))}
    </div>
  );
};

export const FontFamilySelector = ({ value, onChange, customFonts = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const baseFontFamilies = useMemo(() => {
    const clone = {};
    Object.entries(FONT_GROUPED_OPTIONS).forEach(([group, fonts]) => {
      clone[group] = fonts.slice();
    });
    return clone;
  }, []);

  const mergedFontFamilies = useMemo(() => {
    const uniqueCustomFonts = Array.from(
      new Set(
        (customFonts || [])
          .map(font => (typeof font === 'string' ? font.trim() : ''))
          .filter(Boolean)
      )
    );
    if (!uniqueCustomFonts.length) return baseFontFamilies;
    const clone = Object.entries(baseFontFamilies).reduce((acc, [group, fonts]) => {
      acc[group] = fonts.slice();
      return acc;
    }, {});
    const existing = new Set(
      Object.values(clone)
        .flat()
        .map(font => font.toLowerCase())
    );
    const ensureFontListed = fontName => {
      if (!fontName) return;
      const key = fontName.toLowerCase();
      if (existing.has(key)) return;
      existing.add(key);
      clone.Custom = [...(clone.Custom || []), fontName];
    };
    uniqueCustomFonts.forEach(ensureFontListed);
    ensureFontListed(value);
    return clone;
  }, [baseFontFamilies, customFonts, value]);

  const filteredFonts = useMemo(() => {
    const source = mergedFontFamilies;
    if (!searchTerm) return source;
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = {};
    for (const group in source) {
      const fonts = source[group].filter(font =>
        font.toLowerCase().includes(lowerSearch)
      );
      if (fonts.length > 0) {
        filtered[group] = fonts;
      }
    }
    return filtered;
  }, [searchTerm, mergedFontFamilies]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Font Family
      </label>
      <input
        type="text"
        placeholder="Search fonts..."
        className="form-input mb-2"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
      />
      <select value={value} onChange={onChange} className="form-select">
        {(() => {
          const seen = new Set();
          return Object.entries(filteredFonts).map(([group, fonts]) => {
            const options = fonts
              .filter(font => {
                const key = font.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              })
            .map(font => (
              <option key={`${group}-${font}`} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ));
            if (!options.length) return null;
            return (
              <optgroup key={group} label={group}>
                {options}
              </optgroup>
            );
          });
        })()}
      </select>
    </div>
  );
};

export const PreviewToggle = ({ view, setView }) => (
  <div className="flex bg-gray-200 p-1 rounded-lg">
    <button
      onClick={() => setView('desktop')}
      className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${view === 'desktop' ? 'bg-white text-primary shadow' : 'text-gray-600'}`}
    >
      Desktop
    </button>
    <button
      onClick={() => setView('mobile')}
      className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${view === 'mobile' ? 'bg-white text-primary shadow' : 'text-gray-600'}`}
    >
      Mobile
    </button>
  </div>
);

export const ResponsiveDeviceToggle = ({ value, onChange, variant = 'default' }) => {
  const active = DEVICE_PRESETS[value] ? value : DEVICE_ORDER[0];
  const isCompact = variant === 'compact';

  return (
    <div
      className={`inline-flex rounded-xl border border-slate-200 bg-white/80 shadow-sm ${
        isCompact ? 'p-0.5 gap-1' : 'p-1 gap-1'
      }`}
    >
      {DEVICE_ORDER.map(key => {
        const preset = DEVICE_PRESETS[key];
        const selected = key === active;
        return (
          <button
            key={key}
            type="button"
            onClick={() => {
              if (!selected && typeof onChange === 'function') {
                onChange(key);
              }
            }}
            className={`${
              selected
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-600 hover:text-primary hover:bg-slate-100'
            } ${
              isCompact
                ? 'px-2 py-1 text-[11px] font-semibold rounded-lg'
                : 'px-3 py-1.5 text-xs font-semibold rounded-lg'
            } transition-colors`}
            aria-pressed={selected}
            title={preset.description}
          >
            <span>{preset.label}</span>
            {!isCompact && (
              <span className="block text-[10px] font-normal text-slate-400">
                {preset.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export { default as MobilePreviewFrame } from './MobilePreviewFrame';
